import { capitalise } from "../people";
import { categoryLabel, formatMonthlyCost, noticeInfo } from "../suppliers";
import type { SupplierView } from "../types";
import { PersonRef } from "./PersonRef";

/**
 * The firm's suppliers as the contract file shows them (14 §2). Terms are facts; service and lock-in come from the
 * server as words. A notice date that is close gets a glyph and words, never colour alone.
 */
export function SuppliersPanel({ suppliers, today, youId }: { suppliers: SupplierView[]; today: string; youId: string }) {
  return (
    <section className="panel" aria-labelledby="sup-title">
      <h2 id="sup-title" className="panel-title">Suppliers</h2>
      <p className="panel-note">
        A supplier nobody owns tends to drift: contracts renew on their own terms, prices creep up and problems land
        on whoever picks up the phone.
      </p>
      {suppliers.length === 0 ? (
        <p>There are no supplier contracts on file.</p>
      ) : (
        <>
          <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="sup-caption">
            <table className="acc-table sup-table">
              <caption id="sup-caption" className="visually-hidden">Suppliers: who owns each relationship, and the contract terms on file</caption>
              <thead>
                <tr>
                  <th scope="col">Supplier</th>
                  <th scope="col">What it is</th>
                  <th scope="col">Owner</th>
                  <th scope="col">Renews automatically</th>
                  <th scope="col">Notice by</th>
                  <th scope="col">Monthly cost</th>
                  <th scope="col">Lock-in</th>
                  <th scope="col">Service</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => {
                  const n = noticeInfo(s.noticeDeadline, today);
                  return (
                    <tr key={s.id}>
                      <th scope="row">{s.name}</th>
                      <td>{categoryLabel(s.category)}</td>
                      <td>
                        {!s.owner ? "Nobody"
                          : s.owner.id === youId ? "You"
                          : <PersonRef personId={s.owner.id} person={s.owner} />}
                      </td>
                      <td>{!s.termsKnown ? <span className="muted">Not known</span> : s.autoRenew ? "Yes" : "No"}</td>
                      <td>
                        {!s.termsKnown ? <span className="muted">Not known</span>
                          : n.date ? <span className="sup-date">{n.date}</span> : <span className="muted">None on file</span>}
                        {n.note && (
                          <span className={n.soon ? "sup-notice sup-notice--soon" : "sup-notice"}>
                            {n.soon && <span aria-hidden="true">◷ </span>}
                            {n.note}
                          </span>
                        )}
                      </td>
                      <td className="num">{formatMonthlyCost(s.monthlyCost)}</td>
                      <td>{capitalise(s.lockIn)}</td>
                      <td>{capitalise(s.service)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="panel-note">Service is how it seems from inside the firm, not a measured figure. Renewal terms and notice dates are known once someone owns the supplier or the contract has been reviewed.</p>
        </>
      )}
    </section>
  );
}
